import {
  faChevronLeft,
  faLifeRing,
  faEnvelope,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-light-svg-icons';
import { useContext, JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Link } from '../shared/components/link/Link';
import ChevronBackBtn from '../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import { AuthContext } from '../shared/context/auth-context';
import { HeaderNavigation } from '../shared/headerNavigation/HeaderNavigation';
import useDocumentTitle from '../shared/hooks/title-hook';
import './AccessibilityStatement.css';

function AccessibilityStatement(): JSX.Element {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Set document title for non-logged-in users
  // Logged-in users get title from HeaderNavigation via usePageTitle
  useDocumentTitle(
    !auth.isLoggedIn ? `${t('page.title.accessibilityStatement')} - Champion Health` : '',
    t('accessibility.statement.description')
  );

  const handleBackNavigation = () => {
    // Check if user came from profile page
    const { referrer } = document;
    const { from } = (location.state as { from?: string }) || {};
    const profileUrl = `${window.location.origin}/profile`;
    const cameFromProfile =
      referrer.includes('/profile') ||
      (from && typeof from === 'string' && from.includes('/profile')) ||
      (window.history.length > 1 && referrer.includes(profileUrl));

    if (cameFromProfile) {
      // Return to profile page
      navigate('/profile');
      return;
    }

    // Check if there's browser history to go back
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    // Fallback to access-stage logic
    if (!auth.isLoggedIn) {
      navigate('/code');
    } else if (auth.tags && auth.tags.length > 0) {
      navigate('/for-you');
    } else {
      navigate('/assessment');
    }
  };

  return (
    <div id="main_container" className="foryou accessibility-statement-container platform">
      <div className="wrap-screen">
        {auth.isLoggedIn && (
          <HeaderNavigation
            bottomBarActionButton={{
              ariaLabel: t('nav.button.back'),
              onClick: handleBackNavigation,
              icon: {
                faIconDefinition: faChevronLeft,
                size: 'lg',
              },
              variant: 'icon-only',
              className: 'back-button',
            }}
          />
        )}

        <div className="accessibility-statement">
          {!auth.isLoggedIn && (
            <div className="accessibility-statement__back-button-container">
              <ChevronBackBtn onClick={handleBackNavigation} />
            </div>
          )}
          <section
            className="accessibility-statement__section  accessibility-statement__section--first"
            aria-labelledby="accessibility-title"
          >
            <h1 id="accessibility-title" className="accessibility-statement__title">
              {t('accessibility.statement.title')}
            </h1>
            <p className="accessibility-statement__intro">{t('accessibility.statement.intro')}</p>
          </section>

          <section
            className="accessibility-statement__section accessibility-statement__section--content"
            aria-labelledby="compliance-status-heading"
          >
            <div className="accessibility-statement__section-inner">
              <h2 id="compliance-status-heading" className="accessibility-statement__subtitle">
                {t('accessibility.statement.complianceStatus.title')}
              </h2>
              <div className="accessibility-statement__content">
                <p>{t('accessibility.statement.complianceStatus.scope')}</p>
                <p>{t('accessibility.statement.complianceStatus.statement')}</p>
                <p>{t('accessibility.statement.complianceStatus.commitment')}</p>
              </div>
            </div>
          </section>

          <section
            className="accessibility-statement__section accessibility-statement__section--content"
            aria-labelledby="non-accessible-content-heading"
          >
            <div className="accessibility-statement__section-inner">
              <h2 id="non-accessible-content-heading" className="accessibility-statement__subtitle">
                {t('accessibility.statement.nonAccessibleContent.title')}
              </h2>
              <div className="accessibility-statement__content">
                <p>{t('accessibility.statement.nonAccessibleContent.intro')}</p>
                <h3 className="accessibility-statement__subheading">
                  {t('accessibility.statement.nonAccessibleContent.userway.title')}
                </h3>
                <p>{t('accessibility.statement.nonAccessibleContent.userway.content')}</p>
                <h3 className="accessibility-statement__subheading">
                  {t('accessibility.statement.nonAccessibleContent.contrastModes.title')}
                </h3>
                <p>{t('accessibility.statement.nonAccessibleContent.contrastModes.content')}</p>
              </div>
            </div>
          </section>

          <section
            className="accessibility-statement__section accessibility-statement__section--content"
            aria-labelledby="environments-heading"
          >
            <div className="accessibility-statement__section-inner">
              <h2 id="environments-heading" className="accessibility-statement__subtitle">
                {t('accessibility.statement.environments.title')}
              </h2>
              <div className="accessibility-statement__content">
                <p>{t('accessibility.statement.environments.content')}</p>
              </div>
            </div>
          </section>

          <section
            className="accessibility-statement__section accessibility-statement__section--content"
            aria-labelledby="features-heading"
          >
            <div className="accessibility-statement__section-inner">
              <h2 id="features-heading" className="accessibility-statement__subtitle">
                {t('accessibility.statement.features.title')}
              </h2>
              <div className="accessibility-statement__content">
                <p>{t('accessibility.statement.features.userway')}</p>
              </div>
            </div>
          </section>

          <section
            className="accessibility-statement__section accessibility-statement__section--content"
            aria-labelledby="preparation-heading"
          >
            <div className="accessibility-statement__section-inner">
              <h2 id="preparation-heading" className="accessibility-statement__subtitle">
                {t('accessibility.statement.preparation.title')}
              </h2>
              <div className="accessibility-statement__content">
                <p>{t('accessibility.statement.preparation.dates')}</p>
                <p>{t('accessibility.statement.preparation.method')}</p>
                <p>{t('accessibility.statement.preparation.auditor')}</p>
                <p>
                  <Link
                    href="https://digitalaccessibilitycentre.org/index.php/Champion-Health/"
                    text={t('accessibility.statement.preparation.certificate')}
                    icon={{
                      faIconDefinition: faArrowUpRightFromSquare,
                      size: 'sm',
                    }}
                    iconPosition="left"
                    className="accessibility-statement__link"
                    ariaLabel={t('accessibility.statement.preparation.certificate')}
                    target="_blank"
                  />
                </p>
              </div>
            </div>
          </section>

          <section
            className="accessibility-statement__section accessibility-statement__section--content"
            aria-labelledby="feedback-heading"
          >
            <div className="accessibility-statement__section-inner">
              <h2 id="feedback-heading" className="accessibility-statement__subtitle">
                {t('accessibility.statement.feedback.title')}
              </h2>
              <div className="accessibility-statement__content">
                <p>{t('accessibility.statement.feedback.intro')}</p>
                <p>{t('accessibility.statement.feedback.contactMethods')}</p>
                <ul
                  className="accessibility-statement__contact-links"
                  aria-label={t('accessibility.statement.feedback.contactMethodsLabel')}
                >
                  <li>
                    <Link
                      href="mailto:support@championhealth.co.uk"
                      text={t('accessibility.statement.feedback.email')}
                      icon={{
                        faIconDefinition: faEnvelope,
                        size: 'sm',
                      }}
                      iconPosition="left"
                      className="accessibility-statement__link"
                      ariaLabel={t('accessibility.statement.feedback.email')}
                    />
                  </li>
                  <li>
                    <Link
                      href="/support"
                      text={t('accessibility.statement.feedback.support')}
                      icon={{
                        faIconDefinition: faLifeRing,
                        size: 'sm',
                      }}
                      iconPosition="left"
                      className="accessibility-statement__link"
                      ariaLabel={t('accessibility.statement.feedback.support')}
                    />
                  </li>
                </ul>
                <p>{t('accessibility.statement.feedback.responseTime')}</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AccessibilityStatement;
